import PluginBase from '../base';
import { getImportWithDefaultExportThen } from '../../lib/import_utils';

// Keep one module entry per line so adding modules stays a clean, one-line diff.
//
// Each module is imported LAZILY (`() => import(...)`), so webpack code-splits
// it into its own chunk that is only fetched at runtime when the module is
// enabled in settings. Brands that never enable a module (e.g. Vox brands and
// `adhesion`) never download its chunk — it is not built into `concert-ads.js`.
// prettier-ignore
const modules = [
  { name: 'pmcAdBridge', doImport: () => import('./pmc-ad-bridge') },
  { name: 'adhesion', doImport: () => import('./adhesion') },
  { name: 'browsi', doImport: () => import('./browsi') },
  { name: 'pmcSkin', doImport: () => import(/* webpackChunkName: "plugin-pmc-skin" */ './pmc-skin') },
  { name: 'pmcAdmiral', beforeAdsRequested: true, doImport: () => import('./pmc-admiral') },
];

export default class extends PluginBase {
  onSettingsLoaded() {
    if (!this.app.settings.plugins || this.app.hasReinstalled) return;

    const activeModules = modules.filter(module => {
      const config = this.app.settings.plugins[module.name];

      if (!config) return false;

      // Found in settings → enabled by default; use the explicit `enabled` flag when present.
      return config.enabled ?? true;
    });

    activeModules.forEach(module => {
      // Record each active plugin's load promise in `app.dynamicPlugins`, keyed by module name.
      // `onSettingsLoaded` fires (synchronously) during `bootDependenciesOfSettings`, BEFORE
      // `install()` runs its slot-insertion loop and before any slot's `render()`, so the promise
      // is in the map in time for a slot to await it. A slot pulls only the plugins it actually
      // needs (see GamAdSlot#render, which awaits the `adhesion` entry for the adhesion slot only)
      // rather than every slot blocking on a shared list — so no non-adhesion slot is delayed by
      // the adhesion library, and pmcAdBridge (reached only post-render) delays nothing.
      //
      // The promise resolves with the constructed plugin instance (or `undefined` if the import
      // failed — the helper swallows load errors), so a consumer that needs the instance itself can
      // `dynamicPlugins[name]?.then(plugin => …)`; a consumer that only needs the "loaded"
      // signal (e.g. the adhesion render wait) simply awaits it and ignores the value.
      const ready = getImportWithDefaultExportThen(
        module.doImport(),
        `${module.name}`,
        defaultExport => new defaultExport({ app: this.app })
      );
      this.app.dynamicPlugins[module.name] = ready;
      // Page-level keys have to be on the first GAM request. This chunk is async, and the
      // classifier wait is often already finished, so hold every slot until prepare() has run.
      // Slot-scoped plugins stay off this list (adhesion is awaited by its own slot only).
      if (module.beforeAdsRequested) {
        this.app.beforeAdsRequested.push(ready);
      }
    });
  }
}
